import React, { useState } from 'react';
import styled from 'styled-components';
import { colors } from '../../styles/colors.ts';
import ScrollToTop from '../ScrollToTop.js';
import Toast from '../Toast.js';
import usePageMeta from '../../hooks/usePageMeta';

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  flex-direction: row;
  gap: 4rem;
  padding: 8rem 2rem;
  width: 100%;
  max-width: 1400px;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 2rem;
    padding: 8rem 1rem;
  }
`;  

const ImageContainer = styled.div`
  display: flex;
  overflow: hidden;
  max-width: calc(100% - 200px);
  width: 100%;

  @media (max-width: 1024px) {
    border-radius: 1rem;
    width: 100%;
    height: 560px;
    max-width: 100%;
  }

  @media (max-width: 768px) {
    width: 100%;
    height: 400px;
  }
`;

const Image = styled.img`
  border-radius: 8rem 0 0 0;
  min-width: 100%;
  max-height: 100%;
  object-fit: cover;
  filter: grayscale(100);

  @media (max-width: 1024px) {
    border-radius: 1rem;
  }
`;

const InfoContainer = styled.div`
  width: 280px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 800px;
  
  @media (max-width: 1024px) {
    max-width: 600px;
    width: 100%;
  }
`;


const Name = styled.h1`
  font-size: 1.8rem;
  line-height: 1.2;
  color: ${colors.black};
  font-weight: 100;
  width: 100%;
  max-width: 100%;
  word-break: break-all;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Title = styled.h2`
  font-size: 0.8rem;
  font-weight: 500;
  color: ${colors.neutral[400]};
`;

const Description = styled.p`
  font-size: .8rem;
`;

const ContactButtonsContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  flex-wrap: wrap;
`;

const ContactButtonEmail = styled.div`
  display: flex;
  align-items: center;
  gap: 0.2rem;
`;

const CopyAction = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  text-decoration: none;
  background: none;
  border: none;
  padding: 0.2rem 0.5rem;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.2);

    & svg {
      pointer-events: none;

      & path {
        fill: ${colors.black};
      }
    }
  }

  & svg {
    height: 0.8rem;
    width: 0.8rem;
    
    & path {
      fill: ${colors.neutral[400]};
    }
  }
`;

const ContactButton = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.2rem 0.5rem;
  text-decoration: none;
  color: inherit;
  border: 1px solid ${colors.neutral[300]};
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${colors.neutral[100]};
    color: ${colors.black};
    transform: scale(1.05);
  }

  & > * {
    pointer-events: none;
  }

  & svg {
    height: 0.8rem;
    width: 0.8rem;
    
    & path {
      fill: ${colors.neutral[400]};
    }
  }
  
  & p {
    font-size: 0.8rem;
    margin: 0;
  }
`;


function About() {
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastPosition, setToastPosition] = useState({ x: 0, y: 0 });

  usePageMeta({
    title: 'Guzmán Cura - Product Designer',
    description: 'Product Designer with expertise in UX/UI design and a strong understanding of frontend development. Passionate about creating intuitive, high-impact interfaces that drive exceptional user experiences.',
    keywords: ['UX designer', 'UI designer', 'product designer', 'UX/UI portfolio', 'web designer', 'user experience', 'user interface', 'frontend-aware designer', 'design systems', 'digital product design', 'Guzmán Cura', 'freelance designer', 'UX/UI Montevideo', 'UI expert', 'UX professional', 'Figma', 'UX strategy'],
  });

  const handleCopyEmail = async (e) => {
    // Set position first, before showing toast
    setToastPosition({ x: e.clientX, y: e.clientY });
    
    try {
      await navigator.clipboard.writeText('guzmancura@gmail.com');
      setToastMessage('Email address copied');
      setShowToast(true);
    } catch (err) {
      setToastMessage('Failed to copy email address');
      setShowToast(true);
    }
  };

  const handleToastClose = () => {
    setShowToast(false);
  };

  return (
      <Container>
        <ScrollToTop />
        <Toast 
          isVisible={showToast}
          message={toastMessage}
          duration={3000}
          onClose={handleToastClose}
          initialPosition={toastPosition}
        />
        <ImageContainer>
          <Image src="/assets/hero2.png" alt="About Image" />
        </ImageContainer>
        <InfoContainer>
        {/* <Tag><p>{'//'}ABOUT</p></Tag> */}
          <Name>Guzmán <br/> Cura</Name>
          <Title>UX/UI - Product Designer</Title>
          <Description>
            Product Designer with expertise in UX/UI design and a strong understanding of frontend development. Passionate about creating intuitive, high-impact interfaces that drive exceptional user experiences.
            <br /><br />
            Experienced in building and maintaining comprehensive design systems, collaborating closely with developers to ensure seamless implementation. My technical knowledge bridges the gap between design and development, enabling efficient and scalable solutions.
            <br /><br />
            Skilled in leading end-to-end design projects, working directly with clients and stakeholders to align business goals with user needs.
          </Description>
          <ContactButtonsContainer>
            {/* <ContactButton href="/assets/cv.svg" download>
              <svg width="9" height="13" viewBox="0 0 9 13">
                <path d="M5.25 3.38721V0.199707H0.5625C0.250781 0.199707 0 0.450488 0 0.762207V11.6372C0 11.9489 0.250781 12.1997 0.5625 12.1997H8.4375C8.74922 12.1997 9 11.9489 9 11.6372V3.94971H5.8125C5.50313 3.94971 5.25 3.69658 5.25 3.38721ZM9 3.05674V3.19971H6V0.199707H6.14297C6.29297 0.199707 6.43594 0.258301 6.54141 0.36377L8.83594 2.66064C8.94141 2.76611 9 2.90908 9 3.05674Z"/>
              </svg>
              <p>CV</p>
            </ContactButton> */}
            <ContactButton href="https://linkedin.com/in/guzmancura" target="_blank" rel="noopener noreferrer">
              <svg width="12" height="13" viewBox="0 0 12 13">
                <path d="M11.1429 0.199707H0.854464C0.383036 0.199707 0 0.5881 0 1.06489V11.3345C0 11.8113 0.383036 12.1997 0.854464 12.1997H11.1429C11.6143 12.1997 12 11.8113 12 11.3345V1.06489C12 0.5881 11.6143 0.199707 11.1429 0.199707ZM3.62679 10.4854H1.84821V4.75864H3.62946V10.4854H3.62679ZM2.7375 3.97649C2.16696 3.97649 1.70625 3.5131 1.70625 2.94524C1.70625 2.37739 2.16696 1.91399 2.7375 1.91399C3.30536 1.91399 3.76875 2.37739 3.76875 2.94524C3.76875 3.51578 3.30804 3.97649 2.7375 3.97649ZM10.2937 10.4854H8.51518V7.69971C8.51518 7.03542 8.50179 6.18096 7.59107 6.18096C6.66429 6.18096 6.52232 6.90417 6.52232 7.65149V10.4854H4.74375V4.75864H6.45V5.54078H6.47411C6.7125 5.09078 7.29375 4.61667 8.15893 4.61667C9.95893 4.61667 10.2937 5.80328 10.2937 7.34614V10.4854Z" />
              </svg>
              <p>LinkedIn</p>
            </ContactButton>
            <ContactButton href="https://wellfound.com/guzmancura " target="_blank" rel="noopener noreferrer">
              <svg width="16" height="9" viewBox="0 0 16 9">
                <path d="M15.9987 1.61837C16.0407 0.69904 14.924 0.0343729 14.1353 0.50904C13.3153 0.907706 13.254 2.18904 14.0313 2.66504C14.8313 3.23971 16.028 2.60504 15.9987 1.61837ZM15.9987 6.78371C16.0407 5.86371 14.924 5.19904 14.1353 5.67371C13.3153 6.07237 13.254 7.35371 14.0313 7.82971C14.8313 8.40504 16.028 7.76971 15.9987 6.78304V6.78371ZM2.332 7.95837L0 0.442373H2.17133L3.54867 5.84637L5.04533 0.442373H7.22333L8.71933 5.84637L10.096 0.442373H12.2673L9.90733 7.95837H7.59333C7.10667 6.14971 6.62 4.34104 6.13467 2.53171L4.646 7.95837H2.33333H2.332Z" />
              </svg>
              <p>Wellfound</p>
            </ContactButton>
            <ContactButtonEmail>
              <ContactButton href="mailto:guzmancura@gmail.com" target="_blank" rel="noopener noreferrer">
                <svg width="25" height="21" viewBox="0 0 25 21">
                  <path d="M2.5 20.3334C1.8125 20.3334 1.22396 20.0886 0.734375 19.599C0.244792 19.1094 0 18.5209 0 17.8334V2.83337C0 2.14587 0.244792 1.55733 0.734375 1.06775C1.22396 0.578166 1.8125 0.333374 2.5 0.333374H22.5C23.1875 0.333374 23.776 0.578166 24.2656 1.06775C24.7552 1.55733 25 2.14587 25 2.83337V17.8334C25 18.5209 24.7552 19.1094 24.2656 19.599C23.776 20.0886 23.1875 20.3334 22.5 20.3334H2.5ZM12.5 11.5834L22.5 5.33337V2.83337L12.5 9.08337L2.5 2.83337V5.33337L12.5 11.5834Z" />              
                </svg>
                <p>Email</p>
              </ContactButton>
              <CopyAction onClick={handleCopyEmail}>
                <svg width="25" height="28" viewBox="0 0 25 28">
                  <path d="M3.19282 28C2.49808 28 1.90335 27.7508 1.40861 27.2523C0.913872 26.7538 0.666504 26.1545 0.666504 25.4545V7.63636H3.19282V25.4545H17.0876V28H3.19282ZM8.24545 22.9091C7.55071 22.9091 6.95598 22.6598 6.46124 22.1614C5.9665 21.6629 5.71914 21.0636 5.71914 20.3636V2.54545C5.71914 1.84545 5.9665 1.24621 6.46124 0.747727C6.95598 0.249242 7.55071 0 8.24545 0H17.0876L24.6665 7.63636V20.3636C24.6665 21.0636 24.4191 21.6629 23.9244 22.1614C23.4297 22.6598 22.8349 22.9091 22.1402 22.9091H8.24545ZM15.8244 8.90909H22.1402L15.8244 2.54545V8.90909Z" />
                </svg>
              </CopyAction>
            </ContactButtonEmail>
          </ContactButtonsContainer>
        </InfoContainer>
      </Container>
  );
}

export default About;